/**
 * Digital Role - Validate Pillar
 *
 * Validação de valores de armazenamento digital.
 * ZERO dependências de outros pilares - totalmente standalone.
 *
 * @example
 * import { validateDigital, isValidDigital } from '@attrx/role-morphic/digital/validate';
 *
 * validateDigital(100);   // { valid: true, errors: [] }
 * validateDigital(-5);    // { valid: false, errors: ['Digital storage cannot be negative'] }
 * isValidDigital(100);    // true
 */

// =============================================================================
// TYPES
// =============================================================================

export interface DigitalValidationConfig {
  /** Valor mínimo permitido (default: 0) */
  min?: number;
  /** Valor máximo permitido */
  max?: number;
  /** Mensagem de erro para valor abaixo do mínimo */
  minError?: string;
  /** Mensagem de erro para valor acima do máximo */
  maxError?: string;
  /** Permitir valores negativos (default: false) */
  allowNegative?: boolean;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

// =============================================================================
// DEFAULT CONFIG
// =============================================================================

export const DIGITAL_VALIDATION_CONFIG: DigitalValidationConfig = {
  min: 0,
  minError: "Digital storage cannot be negative",
  allowNegative: false,
};

// =============================================================================
// VALIDATE
// =============================================================================

/**
 * Valida um valor de armazenamento digital
 *
 * @param value - Valor a validar
 * @param config - Configuração de validação (opcional)
 * @returns Resultado da validação com lista de erros
 *
 * @example
 * validateDigital(100);                    // { valid: true, errors: [] }
 * validateDigital(-5);                     // { valid: false, errors: ['Digital storage cannot be negative'] }
 * validateDigital(-5, { allowNegative: true }); // { valid: true, errors: [] }
 */
export function validateDigital(
  value: unknown,
  config: DigitalValidationConfig = DIGITAL_VALIDATION_CONFIG,
): ValidationResult {
  const errors: string[] = [];

  // Must be a number
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return {
      valid: false,
      errors: ["Value must be a finite number"],
    };
  }

  // Merge with defaults
  const effectiveConfig = { ...DIGITAL_VALIDATION_CONFIG, ...config };

  // Check negative
  if (!effectiveConfig.allowNegative && value < 0) {
    errors.push(effectiveConfig.minError || "Digital storage cannot be negative");
  }

  // Check min (if allowNegative is true and min is set)
  if (
    effectiveConfig.allowNegative &&
    effectiveConfig.min !== undefined &&
    value < effectiveConfig.min
  ) {
    errors.push(effectiveConfig.minError || `Value must be at least ${effectiveConfig.min}`);
  }

  // Check max
  if (effectiveConfig.max !== undefined && value > effectiveConfig.max) {
    errors.push(effectiveConfig.maxError || `Value must be at most ${effectiveConfig.max}`);
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Verifica se valor de armazenamento digital é válido (helper booleano)
 *
 * @param value - Valor a validar
 * @param config - Configuração de validação (opcional)
 * @returns true se válido, false caso contrário
 *
 * @example
 * isValidDigital(100);  // true
 * isValidDigital(-5);   // false
 */
export function isValidDigital(
  value: unknown,
  config?: DigitalValidationConfig,
): boolean {
  return validateDigital(value, config).valid;
}
